import React, { useEffect, useState, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Facebook, Twitter, Linkedin, Heart, MessageSquare } from 'lucide-react';
import { blogPosts } from '../data/blogData';
const BlogPostPage = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<{
    name: string;
    text: string;
    date: string;
  }[]>([{
    name: 'Sarah M.',
    text: 'This was exactly what I needed to read today. Thank you for the practical advice!',
    date: '2 days ago'
  }, {
    name: 'Blessing O.',
    text: "I tried the tips with my 8-month-old and they worked wonderfully. She's now enjoying more variety in her diet.",
    date: '1 week ago'
  }]);
  // Find the blog post with the matching ID
  const post = blogPosts.find(post => post.id === id);
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  if (!post) {
    return <div className="container mx-auto px-4 py-16 text-center" data-id="element-225">
        <h2 className="text-2xl font-bold text-brown mb-4" data-id="element-226">
          Blog Post Not Found
        </h2>
        <p className="mb-8" data-id="element-227">The blog post you're looking for doesn't exist.</p>
        <Link to="/blog" className="inline-flex items-center text-brown hover:text-brown-dark" data-id="element-228">
          <ArrowLeft size={16} className="mr-2" data-id="element-229" />
          Back to Education Hub
        </Link>
      </div>;
  }
  // Format content with proper paragraphs
  const formattedContent = post.content?.split('\n').map((paragraph, index) => <p key={index} className={`mb-4 ${paragraph.startsWith('🌱') || paragraph.startsWith('💡') || paragraph.startsWith('🍲') ? 'font-medium' : ''}`} data-id="element-230">
      {paragraph}
    </p>);
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (comment.trim()) {
      setComments([{
        name: 'You',
        text: comment,
        date: 'Just now'
      }, ...comments]);
      setComment('');
    }
  };
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post.title;
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      default:
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
    }
  };
  return <div className="bg-cream py-12" data-id="element-231">
      <div className="container mx-auto px-4" data-id="element-232">
        {/* Back button */}
        <div className="mb-8" data-id="element-233">
          <button onClick={() => navigate('/blog')} className="inline-flex items-center text-brown hover:text-brown-dark" data-id="element-234">
            <ArrowLeft size={16} className="mr-2" data-id="element-235" />
            Back to Education Hub
          </button>
        </div>
        {/* Article */}
        <article className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8" data-id="element-236">
          {/* Category and date */}
          <div className="flex justify-between items-center mb-4" data-id="element-237">
            <span className="text-sm text-brown bg-beige px-2 py-1 rounded-full" data-id="element-238">
              {post.category}
            </span>
            <span className="text-sm text-gray-500" data-id="element-239">{post.date}</span>
          </div>
          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-brown mb-6" data-id="element-240">
            {post.title}
          </h1>
          {/* Featured image */}
          <div className="mb-8" data-id="element-241">
            <img src={post.imageUrl} alt={post.title} className="w-full h-auto rounded-lg object-cover" data-id="element-242" />
          </div>
          {/* Content */}
          <div className="prose max-w-none text-gray-700" data-id="element-243">
            {formattedContent}
          </div>
          {/* Engagement section */}
          <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-244">
            <div className="flex justify-between items-center" data-id="element-245">
              <button className={`flex items-center gap-2 ${liked ? 'text-red-500' : 'text-gray-500'}`} onClick={() => setLiked(!liked)} data-id="element-246">
                <Heart size={20} fill={liked ? 'currentColor' : 'none'} data-id="element-247" />
                <span data-id="element-248">{liked ? 'Liked' : 'Like'}</span>
              </button>
              <div className="flex items-center gap-4" data-id="element-249">
                <span className="text-gray-500" data-id="element-250">Share:</span>
                <button onClick={() => handleShare('facebook')} className="text-blue-600 hover:text-blue-800" aria-label="Share on Facebook" data-id="element-251">
                  <Facebook size={18} data-id="element-252" />
                </button>
                <button onClick={() => handleShare('twitter')} className="text-sky-500 hover:text-sky-700" aria-label="Share on Twitter" data-id="element-253">
                  <Twitter size={18} data-id="element-254" />
                </button>
                <button onClick={() => handleShare('linkedin')} className="text-blue-700 hover:text-blue-900" aria-label="Share on LinkedIn" data-id="element-255">
                  <Linkedin size={18} data-id="element-256" />
                </button>
                <button onClick={() => handleShare('copy')} className="text-gray-500 hover:text-gray-700" aria-label="Copy link" data-id="element-257">
                  <Share2 size={18} data-id="element-258" />
                </button>
              </div>
            </div>
          </div>
        </article>
        {/* Comments section */}
        <section className="bg-white rounded-lg shadow-md p-6 md:p-8" data-id="element-259">
          <h2 className="text-xl font-bold text-brown mb-6 flex items-center gap-2" data-id="element-260">
            <MessageSquare size={20} data-id="element-261" />
            Comments ({comments.length})
          </h2>
          {/* Comment form */}
          <form onSubmit={handleSubmitComment} className="mb-8" data-id="element-262">
            <div className="mb-4" data-id="element-263">
              <textarea placeholder="Share your thoughts..." className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brown" rows={4} value={comment} onChange={e => setComment(e.target.value)} required data-id="element-264" />
            </div>
            <div className="text-right" data-id="element-265">
              <button type="submit" className="bg-brown text-cream px-6 py-2 rounded-full hover:bg-brown-dark transition-colors duration-300" data-id="element-266">
                Post Comment
              </button>
            </div>
          </form>
          {/* Comments list */}
          <div className="space-y-6" data-id="element-267">
            {comments.map((comment, index) => <div key={index} className="border-b border-gray-100 pb-6 last:border-0" data-id="element-268">
                <div className="flex justify-between mb-2" data-id="element-269">
                  <h3 className="font-medium text-brown" data-id="element-270">{comment.name}</h3>
                  <span className="text-sm text-gray-500" data-id="element-271">{comment.date}</span>
                </div>
                <p className="text-gray-700" data-id="element-272">{comment.text}</p>
              </div>)}
          </div>
        </section>
        {/* Related posts */}
        <section className="mt-12" data-id="element-273">
          <h2 className="text-2xl font-bold text-brown mb-6" data-id="element-274">
            You May Also Like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-275">
            {blogPosts.filter(relatedPost => relatedPost.id !== post.id).slice(0, 3).map(relatedPost => <div key={relatedPost.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-276">
                  <Link to={`/blog/${relatedPost.id}`} data-id="element-277">
                    <img src={relatedPost.imageUrl} alt={relatedPost.title} className="w-full h-48 object-cover" data-id="element-278" />
                    <div className="p-4" data-id="element-279">
                      <h3 className="text-lg font-bold text-brown mb-2" data-id="element-280">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2" data-id="element-281">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                </div>)}
          </div>
        </section>
      </div>
    </div>;
};
export default BlogPostPage;